/**
 * SoundService 상위에서 단어 소리를 연속 재새하는 것을 제어하는 서비스
 */
import { Injectable } from '@angular/core';
import {BehaviorSubject, Observable, Subject, Subscription, throwError, timer} from 'rxjs';
import {delay, filter, finalize} from 'rxjs/operators';

import {GUKEO_API_URL1, MAX_EXAMPLE_VOICE} from '../../defines/protocol';
import {PLAY_VOICE_DELAY, VOICE_INTERVAL, WORD_VOICE_REPLAY_COUNT} from '../../defines/client.const';
import {IsContainHttp, IsEmptyString, IsEmptyVar} from '../../defines/common.func';
import {SimpleWord} from '../../models/simple-word.model';
import {Word} from '../../models/word.model';
import {AudioService} from '../audio/audio.service';
import {Homonym} from '../../models/homonym.model';


@Injectable({
  providedIn: 'root'
})
export class VoiceService {
  private playStream$: Subscription | null = null;
  private waitStream$: Subscription | null = null;

  private playSound$: Subscription | null = null;
  private waitSound$: Subscription | null = null;

  private playWord$: Subscription | null = null;

  private word: Word | SimpleWord | null = null;
  private noExample: number | null = null;
  private exampleVoiceId: string | null = null;

  private homonym: Homonym | null = null;

  public get on(): boolean {
    // console.log('VoiceService::on', this.word, this.noExample);
    return this.word !== null || this.exampleVoiceId !== null;
  }
  public get wordId(): string | null {
    return this.word?.id || null;
  }
  public get noEx(): number | null {
    return this.noExample;
  }
  public get exampleId(): string | null {
    return this.exampleVoiceId;
  }
  public get homonymId(): string | null {
    return this.homonym?.name || null;
  }

  constructor(
    private audioService: AudioService,
  ) { }

  private resetWord$(): void {
    // console.log('resetWord$()', this.playWord$, this.waitSound$);
    if (this.playSound$) {
      this.playSound$.unsubscribe();
      this.playSound$ = null;
    }
    if (this.waitSound$) {
      this.waitSound$.unsubscribe();
      this.waitSound$ = null;
    }
  }

  private resetStream$(): void {
    // console.log('resetStream$()', this.playStream$, this.waitStream$);
    if (this.playStream$) {
      this.playStream$.unsubscribe();
      this.playStream$ = null;
    }
    if (this.waitStream$) {
      this.waitStream$.unsubscribe();
      this.waitStream$ = null;
    }
  }

  public PlaySound(word: SimpleWord | Word, exNum: number | null = null, count: number = 1): Observable<number> {
    this.Stop();

    this.word = word;
    this.noExample = exNum;
    const url = exNum === null ? word.wordVoice : (() => {
      const w = word as Word;
      if (w.exampleVoice && w.exampleVoice.length > exNum) {
        return w.exampleVoice[exNum];
      }
      return null;
    })();
    if (IsEmptyVar(url)) {
      console.error('VoiceService::PlaySound() no sound word, exNum', word, exNum);
      return throwError({detail: 'invalid word and exNum'});
    }

    const playSound$ = new Subject<number>();

    this.playSound$ = timer(PLAY_VOICE_DELAY).subscribe(() => {
      this.playSound$ = this.playSound(url as string, count).pipe(
        finalize(() => {
          this.word = null;
          this.noExample = null;
        })
      ).subscribe(playSound$);
    });

    return playSound$.asObservable();
  }

  public PlayExample(exampleId: string, exampleVoice: string, count: number = 1): Observable<number> {
    this.Stop();

    if (IsEmptyVar(exampleId) || IsEmptyVar(exampleVoice)) {
      console.error('VoiceService::PlayExample() invalid example voice', exampleId, exampleVoice);
      return throwError({detail: 'invalid example voice'});
    }

    this.exampleVoiceId = exampleId;
    const playSound$ = new Subject<number>();

    this.playSound$ = timer(PLAY_VOICE_DELAY).subscribe(() => {
      this.playSound$ = this.playSound(exampleVoice, count).pipe(
        finalize(() => {
          this.exampleVoiceId = null;
        })
      ).subscribe(playSound$);
    });

    return playSound$.asObservable();
  }

  private playSound(url: string, count: number): Observable<number> {
    let countPlay = 0;

    const fullUrl = IsContainHttp(url) ? url : GUKEO_API_URL1 + url;
    console.log('playSound() url =', fullUrl, count);

    return new Observable<number>(observer => {
      this.playStream$ = this.audioService.playStream(fullUrl).pipe(
        filter((event: Event) => event.type === 'ended')
      ).subscribe((event: Event) => {
        console.log('playSound()::audioService.playStream ended', count);
        observer.next(++countPlay);

        if (--count > 0) {
          this.waitStream$ = timer(VOICE_INTERVAL).subscribe(() => {
            this.waitStream$ = null;
            this.audioService.play().then();
          });
        }
        else {
          this.audioService.stop();
          this.playStream$ = null;
          observer.complete();
        }
      }, err => observer.error(err));

      return () => this.resetStream$();
    });
  }

  private playExamples(word: Word): Observable<number> {
    this.word = word;
    return new Observable<any>(observer => {
      const exSubject$ = new BehaviorSubject<number>(0);

      exSubject$.subscribe(noExample => {
        if (noExample < MAX_EXAMPLE_VOICE) {
          this.noExample = noExample;
          const exampleVoice = word.exampleVoice[noExample];
          // console.log('playExamples noExample=', noExample, exampleVoice);

          if (IsEmptyString(exampleVoice)) {
            exSubject$.next(noExample + 1);
          }
          else {
            const fullUrl = IsContainHttp(exampleVoice) ? exampleVoice : GUKEO_API_URL1 + exampleVoice;
            // console.log('playSound() url =', fullUrl);

            this.playStream$ = this.audioService.playStream(fullUrl).pipe(
              filter((event: Event) => event.type === 'ended'),
              finalize(() => {
                // this.audioService.stop();
                this.playStream$ = null;
              })
            ).subscribe(
              (event: Event) => {
                this.playStream$?.unsubscribe();
                observer.next(noExample);
                this.waitStream$ = timer(VOICE_INTERVAL).subscribe(() => {
                  this.waitStream$ = null;
                  exSubject$.next(noExample + 1);
                });
              },
              (err) => {
                // 재생 오류가 나면 기다리지 않고 바로 다음 예문을 재생한다.
                // observer.error(err);
                exSubject$.next(noExample + 1);
              }
            );
          }
        }
        else {
          this.audioService.stop();
          observer.complete();
          exSubject$.complete();
          this.word = null;
          this.noExample = null;
        }
      });

      return () => this.resetStream$();
    });
  }

  public PlayWord(word: Word): Observable<any> {
    this.Stop();

    const playWord$ = new Subject();

    this.playWord$ = timer(PLAY_VOICE_DELAY).subscribe(() => {
      this.playWord$ = this.playWord(word).subscribe(playWord$);
    });

    return playWord$.asObservable();
  }

  private playWord(word: Word): Observable<any> {
    return new Observable<any>(observer => {

      const playExamples = () => {
        this.playSound$ = this.playExamples(word).pipe(
          finalize(() => {
            observer.complete();
            this.playSound$ = null;
            this.word = null;
          })
        ).subscribe(
          count => console.log('voiceService::playExamples count =', count),
          err => console.error('voiceService::playExamples error =', err),
        );
      };

      if (!IsEmptyString(word.wordVoice)) {
        this.word = word;
        this.playSound$ = this.playSound(word.wordVoice, WORD_VOICE_REPLAY_COUNT).pipe(
          finalize(() => {
            console.log('voiceService::PlaySound complete');

            this.waitSound$ = timer(VOICE_INTERVAL).subscribe(() => {
              this.waitSound$ = null;

              playExamples();
            });
          })
        ).subscribe(
          count => console.log('voiceService::PlaySound count =', count),
          err => console.error('voiceService::PlaySound error =', err)
        );
      }
      else {
        playExamples();
      }

      return () => this.resetWord$();
    });
  }

  public PlayHomonym(homonym: Homonym): Observable<number> {
    console.warn('PlayHomonym()', homonym, this.homonym);
    if (this.homonym && homonym.name === this.homonym.name) {
      return throwError({detail: 'Critical Error: PlayHomonym()'});
    }
    this.Stop();

    this.homonym = homonym;
    return this.playHomonym(homonym).pipe(
      finalize(() => {
        this.homonym = null;
        this.Stop();
      })
    );
  }

  private playHomonym(homonym: Homonym): Observable<number> {
    return new Observable<any>(observer => {
      const wordSubject$ = new BehaviorSubject<number>(0);

      wordSubject$.subscribe((noWord: number) => {
        if (noWord < homonym.listWord.length) {
          this.word = homonym.listWord[noWord];
          const wordVoice = this.word.wordVoice;
          // console.log('playHomonym noWord=', noWord, wordVoice);

          if (IsEmptyString(wordVoice)) {
            wordSubject$.next(noWord + 1);
          }
          else {
            const fullUrl = IsContainHttp(wordVoice) ? wordVoice : GUKEO_API_URL1 + wordVoice;

            this.playStream$ = this.audioService.playStream(fullUrl).pipe(
              filter((event: Event) => event.type === 'ended'),
              finalize(() => {
                // this.audioService.stop();
                this.playStream$ = null;
              })
            ).subscribe((event: Event) => {
              this.playStream$?.unsubscribe();
              // console.error('ended', fullUrl);
              observer.next(noWord);
              this.waitStream$ = timer(VOICE_INTERVAL).subscribe(() => {
                this.waitStream$ = null;
                wordSubject$.next(noWord + 1);
              });
            }, err => {
              // observer.error(err);
              wordSubject$.next(noWord + 1);
            });
          }
        }
        else {
          this.audioService.stop();
          observer.complete();
          wordSubject$.complete();
          this.word = null;
        }
      });

      return () => this.resetStream$();
    });
  }

  public Stop(): void {
    if (this.playWord$) {
      this.playWord$.unsubscribe();
      this.playWord$ = null;
    }

    if (this.playSound$) {
      this.playSound$.unsubscribe();
      this.playSound$ = null;
    }

    this.resetStream$();
    this.resetWord$();

    this.word = null;
    this.noExample = null;
    this.exampleVoiceId = null;
    this.homonym = null;
  }

  public HasVoice(word: Word): boolean {
    return !IsEmptyString(word.wordVoice) && word.exampleVoice?.some(v => !IsEmptyString(v));
  }
}
